import React from "react";

import { PricingRuleSheetRow } from "@megaron/crm-contracts";
import { DiffSyncPage, DiffSyncRecord } from "@megaron/diff-sync-webapp";
import { useClientManager } from "@megaron/react-clients";

type PricingRuleDiffValue = Partial<PricingRuleSheetRow> & Record<string, unknown>;

export const PricingRulesSyncDiffPage = () => {
  const client = useClientManager("crm");
  const channel = "sheetsPricingRule";

  const pullQuery = client.diffQuery().useQuery({
    channel,
    isPullable: true,
  });

  const pushQuery = client.diffQuery().useQuery({
    channel,
    isPushable: true,
  });

  const allDiffsQuery = client.diffQuery().useQuery({
    channel,
  });
  const channelQuery = client.diffChannelQuery().useQuery({
    channel,
  });

  const refreshMutation = client.refreshDiffs().useMutation();
  const pullMutation = client.pullDiffs().useMutation();
  const pushMutation = client.pushDiffs().useMutation();

  const refetchDiffs = async () => {
    await Promise.all([pullQuery.refetch(), pushQuery.refetch(), allDiffsQuery.refetch()]);
  };

  const refresh = async () => {
    await refreshMutation.mutateAsync({ channel });
    await refetchDiffs();
  };

  const pull = async (diffIds: string[]) => {
    await pullMutation.mutateAsync({ channel, diffIds });
    await refetchDiffs();
  };

  const push = async (diffIds: string[]) => {
    await pushMutation.mutateAsync({ channel, diffIds });
    await refetchDiffs();
  };

  return (
    <DiffSyncPage<PricingRuleDiffValue>
      title="Synchronizacja Google Sheets - warunki handlowe"
      pullableDiffs={pullQuery.data as DiffSyncRecord<PricingRuleDiffValue>[] | undefined}
      pushableDiffs={pushQuery.data as DiffSyncRecord<PricingRuleDiffValue>[] | undefined}
      errorDiffs={allDiffsQuery.data?.filter(isErrorDiff) as DiffSyncRecord<PricingRuleDiffValue>[] | undefined}
      directionLabels={{
        pull: "Do megarax.net",
        push: "Do google sheets",
      }}
      labels={pricingRuleLabels}
      formatters={pricingRuleFormatters}
      alwaysVisibleColumns={["customerId", "customerName", "priceList"]}
      remoteResourceUrl={channelQuery.data?.remoteResourceUrl}
      isLoading={
        pullQuery.isLoading ||
        pushQuery.isLoading ||
        allDiffsQuery.isLoading ||
        pullQuery.isFetching ||
        pushQuery.isFetching ||
        allDiffsQuery.isFetching
      }
      isRefreshing={refreshMutation.isLoading}
      isExecuting={pullMutation.isLoading || pushMutation.isLoading}
      onRefresh={refresh}
      onPull={pull}
      onPush={push}
    />
  );
};

const isErrorDiff = (diff: DiffSyncRecord<PricingRuleDiffValue>) => !!diff.error;

const pricingRuleLabels = {
  priceList: "Cennik",
  customerId: "Id klienta",
  customerName: "Klient",
  defaultDiscount: "Rabat domyślny",
  notes: "Notatki",
} satisfies Partial<Record<keyof PricingRuleSheetRow, string>>;

const pricingRuleFormatters = {
  defaultDiscount: (value) => (value === null || value === undefined ? "-" : value.toString()),
} satisfies Partial<Record<keyof PricingRuleSheetRow, (value: unknown) => React.ReactNode>>;
